'use client';
import React, { useCallback, useRef } from 'react';
import { Pagination, Navigation } from 'swiper/modules';
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react';

import { CarouselProps } from './types';
import { SliderPaginationButtons } from '../../halfPageSlider/ui/SliderPaginationButtons';

import IconLeft from '@/assets/icons/iconArrowLeft.svg';
import IconRight from '@/assets/icons/iconRightLeft.svg';
import { Button } from '@/shared/components/button';
import { Mods, classNames } from '@/shared/lib/classNames/classNames';

export const Carousel = ({
  children,
  variant = 'main',
  spaceBetweenSlides,
  loop = false,
  countSlider = 1,
  countSlidesPerGroup = 1,
  pagination = false,
  navigation = true,
  navigationButtonsType = 'round',
  customClassNames,
  buttonsPosition = 'center',
  onSlideChange,
  customPagination,
  underlinePagination = false,
  ...props
}: CarouselProps) => {
  const modsButton: Mods = {
    ['rounded-[12px]']: navigationButtonsType === 'square',
    ['rounded-[50%]']: navigationButtonsType === 'round',
    ['top-[calc(50%-56px)]']: !!pagination,
    static: buttonsPosition === 'bottom',
    absolute: buttonsPosition === 'center',
  };

  const modsWrapper: Mods = {
    ['block video_slider']: variant === 'main',
    ['max-md:hidden']: variant === 'mini' || variant === 'middle',
  };

  const sliderRef = useRef(null);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    //@ts-ignore
    sliderRef.current.swiper.slidePrev();
    //@ts-ignore
    sliderRef.current.swiper.setTransition(1000);
  }, []);
  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    //@ts-ignore
    sliderRef.current.swiper.slideNext();
    //@ts-ignore
    sliderRef.current.swiper.setTransition(1000);
  }, []);

  return (
    <div className={classNames({ cls: 'relative w-full', mods: modsWrapper })}>
      <SwiperComponent
        ref={sliderRef}
        {...props}
        loop={loop}
        spaceBetween={spaceBetweenSlides}
        slidesPerView={countSlider}
        onSlideChange={onSlideChange}
        pagination={
          customPagination ||
          (pagination && {
            bulletClass: 'swiper-pagination-bullet pagination-bullet',
            horizontalClass: 'pagination-horizontal',
          })
        }
        modules={[Pagination, Navigation]}
        navigation={navigation}
        slidesPerGroup={countSlidesPerGroup}
        className={classNames({
          cls: 'rounded-20',
          additional: [customClassNames?.main],
        })}
      >
        {children}
      </SwiperComponent>

      {navigation && (
        <div
          className={classNames({
            mods: {
              'mt-6 flex h-[56px] items-center justify-between max-sm:mt-3 max-sm:h-3':
                buttonsPosition === 'bottom',
            },
          })}
        >
          {pagination && (
            <div className="swiper-pagination !w-fit gap-6 max-sm:mx-auto max-sm:!justify-center max-sm:!gap-3"></div>
          )}
          {underlinePagination ? (
            <SliderPaginationButtons
              handleNext={handleNext}
              handlePrev={handlePrev}
            />
          ) : (
            <div className="flex gap-4">
              <Button
                onClick={handlePrev}
                data-testid="button-previous"
                ariaLabel="button-previous"
                className={classNames({
                  cls: 'bg-custom-base-inverse-secondary top-[calc(50%-28px)] left-[-28px] z-10 flex h-14 w-14 cursor-pointer items-center justify-center',
                  mods: modsButton,
                  additional: [customClassNames?.leftButton],
                })}
              >
                <IconLeft />
              </Button>
              <Button
                onClick={handleNext}
                data-testid="button-next"
                ariaLabel="button-next"
                className={classNames({
                  cls: 'bg-custom-base-inverse-secondary top-[calc(50%-28px)] right-[-28px] z-10 flex h-14 w-14 cursor-pointer items-center justify-center',
                  mods: modsButton,
                  additional: [customClassNames?.rightButton],
                })}
              >
                <IconRight />
              </Button>
            </div>
          )}
        </div>
      )}

      {!customPagination && (
        <style jsx global>{`
          .pagination-bullet {
            width: 8px !important;
            height: 8px !important;
            margin: 0 !important;
          }

          .pagination-horizontal {
            gap: 12px;
            height: auto !important;
            margin-top: 20px !important;
          }
        `}</style>
      )}
    </div>
  );
};

Carousel.Item = SwiperSlide;
